import { confirm } from 'components/molecules/Confirmation'
import { easternDay, formatCalendarDate } from 'helpers/dates'
import type { DeviceShelfLifeInfo } from 'store/types'
import routes from '../constants/routes'

// TES-3191: a device whose shelf life is tracked cannot go out once it expires, is retired or is
// missing an accessory, and is retired when it comes back expired.

export type DeviceCheckOutBlock = {
  reason: 'DEVICE_CHECK_OUT_BLOCKED'
  deviceId: number
  blocker: 'RETIRED' | 'EXPIRED' | 'MISSING_FIELDS'
  missingFields: string[]
  expirationDate: string | null
  message: string
}

type CheckedInDevice = {
  serial?: string | null
  label?: string | null
  expirationDate?: string | null
  shelfLife?: DeviceShelfLifeInfo | null
}

export const deviceManagementPath = (deviceId: number | string): string =>
  `${routes.index}${routes.device.root}${routes.upsert}${deviceId}`

// What keeps a device in a check-out list from going out, for its option label.
// A DME checks a device out without its accessories on record; only the lab must record them.
export const checkOutHint = (
  shelfLife?: DeviceShelfLifeInfo | null,
  { requireAccessories = true }: { requireAccessories?: boolean } = {},
): string => {
  if (!shelfLife) return ''
  if (shelfLife.expired) return ' (expired)'
  if (requireAccessories && shelfLife.missingAccessories.length) {
    return ` (missing: ${shelfLife.missingAccessories.map((slot) => slot.label).join(', ')})`
  }
  return ''
}

export const getCheckOutBlock = (error: unknown): DeviceCheckOutBlock | null => {
  const data = (error as { data?: Partial<DeviceCheckOutBlock> } | null)?.data
  return data?.reason === 'DEVICE_CHECK_OUT_BLOCKED' ? (data as DeviceCheckOutBlock) : null
}

/**
 * The notice for a refused check-out: it names what is missing and sends the user to the device
 * in Device Management, which only lab users can open.
 */
export const showCheckOutBlockedNotice = (
  block: DeviceCheckOutBlock,
  { linkToDevice = false }: { linkToDevice?: boolean } = {},
) =>
  confirm('', {
    title: 'Device cannot be checked out',
    yesAction: 'Close',
    noAction: '',
    yesColor: 'secondary',
    description: () => (
      <div data-testid="check-out-blocked">
        <p className="mb-2">{block.message}</p>
        {linkToDevice && block.blocker === 'MISSING_FIELDS' && (
          <a href={deviceManagementPath(block.deviceId)} target="_blank" rel="noreferrer">
            Open the device in Device Management
          </a>
        )}
      </div>
    ),
  }).catch(() => {})

// A refused check-out gets its notice; anything else goes to `fallback` as before.
export const handleCheckOutError = (
  error: unknown,
  fallback: (message?: string) => void,
  options?: { linkToDevice?: boolean },
): void => {
  const block = getCheckOutBlock(error)

  if (block) {
    void showCheckOutBlockedNotice(block, options)
    return
  }

  fallback((error as { data?: { message?: string } } | null)?.data?.message)
}

const isExpiredNow = (device: CheckedInDevice): boolean =>
  !!device.shelfLife?.expired
  || (!!device.expirationDate && easternDay(new Date()) >= device.expirationDate)

/**
 * After a check-in: an expired device has just been retired, and an accessory may be due for
 * replacement. Takes the device as the order showed it before the check-in.
 */
export const showCheckInNotice = (
  device: CheckedInDevice | null | undefined,
  { includeAccessories = true }: { includeAccessories?: boolean } = {},
) => {
  const shelfLife = device?.shelfLife

  if (!device || !shelfLife) return Promise.resolve()

  const name = device.serial || device.label || ''
  const expired = isExpiredNow(device)
  const lines = [
    ...(expired
      ? [
          `Device ${name} has reached its expiration date (${formatCalendarDate(device.expirationDate)}). `
            + 'It has been retired and marked Unavailable, and can no longer be checked out.',
        ]
      : []),
    ...(includeAccessories && !expired
      ? shelfLife.accessoriesDue.map(
          (due) =>
            `The ${due.label} on device ${name} is due for replacement `
            + `(${due.uses} of ${due.limit} uses). Replace it before the device goes out again.`,
        )
      : []),
  ]

  if (!lines.length) return Promise.resolve()

  return confirm('', {
    title: expired ? 'Device retired' : 'Accessory due for replacement',
    yesAction: 'OK',
    noAction: '',
    description: () => (
      <div data-testid="check-in-notice">
        {lines.map((line) => (
          <p key={line} className="mb-2">
            {line}
          </p>
        ))}
      </div>
    ),
  }).catch(() => {})
}
